"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScanLine, Loader2, X } from "lucide-react";
import { useBlockScanner } from "@/hooks/use-block-scanner";
import { parseMultipleBlocks } from "@/lib/block-parser";
import { BlockFormSheet } from "./block-form-sheet";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { formatLocalDateAsUTC } from "@/lib/date-utils";
import { formatTimeWithPreference } from "@/lib/format";
import { useTimeFormat } from "@/lib/time-format-context";

interface ScanBlockButtonProps {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

interface ScannedBlockData {
  id: string;
  date: string;
  shiftStart: string;
  shiftEnd: string;
  basePay: number;
  tip: number;
  noRoutePayout: number;
  station?: string;
}

export function ScanBlockButton({
  variant = "outline",
  size = "default",
  className,
}: ScanBlockButtonProps) {
  const timeFormat = useTimeFormat();
  const { scanMultiple, isScanning, isNative } = useBlockScanner();
  const { toast } = useToast();
  const createBlock = useMutation(api.blocks.createBlock);
  const existingBlocks = useQuery(api.blocks.listBlocks);

  // Single block mode
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [scannedData, setScannedData] = useState<ScannedBlockData | null>(null);

  // Multi-block mode
  const [isMultiSheetOpen, setIsMultiSheetOpen] = useState(false);
  const [scannedBlocks, setScannedBlocks] = useState<ScannedBlockData[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingBlock, setEditingBlock] = useState<ScannedBlockData | null>(null);

  const handleScan = async () => {
    const { results, error: scanError } = await scanMultiple();

    if (results.length === 0) {
      if (scanError) {
        toast({
          title: "Scan Failed",
          description: scanError,
          variant: "destructive",
        });
      }
      return;
    }

    // Combine OCR text from all images
    const allText = results.map(r => r.text).join("\n\n");
    console.log("[ScanBlock] OCR text from", results.length, "image(s):", allText);

    // Parse blocks from all images
    const allParsedBlocks = results.flatMap(r => parseMultipleBlocks(r.text));
    console.log("[ScanBlock] Parsed blocks:", JSON.stringify(allParsedBlocks, null, 2));

    const validBlocks = allParsedBlocks.filter(
      (b) => b.confidence !== "low" || b.basePay
    );

    if (validBlocks.length === 0) {
      toast({
        title: "Couldn't Read Block",
        description: "Unable to extract block details from the image. Try a clearer screenshot.",
        variant: "destructive",
      });
      return;
    }

    const today = formatLocalDateAsUTC(new Date());

    const blocksData: ScannedBlockData[] = validBlocks.map((block, i) => ({
      id: `scanned-${Date.now()}-${i}`,
      date: block.date || today,
      shiftStart: block.shiftStart || "",
      shiftEnd: block.shiftEnd || "",
      basePay: block.basePay || 0,
      tip: 0,
      noRoutePayout: 0,
      station: block.station || undefined,
    }));

    if (blocksData.length === 1) {
      setScannedData(blocksData[0]);
      setIsSheetOpen(true);
    } else {
      setScannedBlocks(blocksData);
      setIsMultiSheetOpen(true);
      toast({
        title: `${blocksData.length} blocks found`,
        description: "Tap rows to edit, then save to log.",
      });
    }
  };

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setScannedData(null);
  };

  const handleMultiSheetClose = () => {
    setIsMultiSheetOpen(false);
    setScannedBlocks([]);
    setEditingBlock(null);
  };

  const handleRemoveBlock = (id: string) => {
    setScannedBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const handleClearAll = () => {
    setScannedBlocks([]);
  };

  const handleEditBlock = (block: ScannedBlockData) => {
    setEditingBlock(block);
  };

  const handleEditSave = (updatedData: {
    date: string;
    shiftStart: string;
    shiftEnd: string;
    basePay: number;
    tip: number;
    noRoutePayout: number;
    station?: string;
  }) => {
    if (!editingBlock) return;

    setScannedBlocks((prev) =>
      prev.map((b) =>
        b.id === editingBlock.id ? { ...b, ...updatedData } : b
      )
    );
    setEditingBlock(null);
  };

  // Check if block is duplicate
  const isDuplicate = (block: ScannedBlockData): boolean => {
    if (!existingBlocks) return false;
    return existingBlocks.some(
      (existing) =>
        existing.date === block.date &&
        existing.shiftStart === block.shiftStart &&
        existing.shiftEnd === block.shiftEnd
    );
  };

  const handleSaveToLog = async () => {
    if (scannedBlocks.length === 0) {
      toast({
        title: "No blocks to save",
        description: "Add some blocks first.",
        variant: "destructive",
      });
      return;
    }

    setIsAdding(true);
    let added = 0;
    let duplicates = 0;
    let failed = 0;

    for (const block of scannedBlocks) {
      // Check for duplicate
      if (isDuplicate(block)) {
        duplicates++;
        continue;
      }

      try {
        await createBlock({
          date: block.date,
          shiftStart: block.shiftStart,
          shiftEnd: block.shiftEnd,
          basePay: block.basePay,
          tip: block.tip,
          noRoutePayout: block.noRoutePayout,
          station: block.station,
        });
        added++;
      } catch (error) {
        console.error("Failed to add block:", error);
        failed++;
      }
    }

    setIsAdding(false);
    handleMultiSheetClose();

    // Show summary
    let message = "";
    if (added > 0) message += `Saved ${added} new block${added > 1 ? "s" : ""}.`;
    if (duplicates > 0) {
      if (message) message += "\n";
      message += `Skipped ${duplicates} duplicate${duplicates > 1 ? "s" : ""} that already existed.`;
    }
    if (failed > 0) {
      if (message) message += "\n";
      message += `${failed} failed to save.`;
    }

    toast({
      title: added > 0 ? "Blocks Saved" : "No New Blocks",
      description: message,
      variant: added > 0 || duplicates > 0 ? "default" : "destructive",
    });
  };

  if (!isNative) {
    return null;
  }

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
  };

  const formatDateShort = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  };

  const formatTimeRange = (start: string, end: string) => {
    if (!start && !end) return "—";
    const formattedStart = start ? formatTimeWithPreference(start, timeFormat) : "?";
    const formattedEnd = end ? formatTimeWithPreference(end, timeFormat) : "?";
    return `${formattedStart} — ${formattedEnd}`;
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleScan}
        disabled={isScanning}
        className={className}
      >
        {isScanning ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Scanning...
          </>
        ) : (
          <>
            <ScanLine className="w-4 h-4 mr-2" />
            Scan Blocks
          </>
        )}
      </Button>

      {/* Single block form sheet */}
      {scannedData && (
        <BlockFormSheet
          mode="create"
          initialData={scannedData}
          open={isSheetOpen}
          onOpenChange={(open) => {
            if (!open) handleSheetClose();
          }}
          onSuccess={handleSheetClose}
        />
      )}

      {/* Edit single block from multi-block view */}
      {editingBlock && (
        <BlockFormSheet
          mode="create"
          initialData={editingBlock}
          open={!!editingBlock}
          onOpenChange={(open) => {
            if (!open) setEditingBlock(null);
          }}
          onSuccess={() => setEditingBlock(null)}
          customSave={handleEditSave}
        />
      )}

      {/* Multi-block review sheet */}
      <Sheet open={isMultiSheetOpen} onOpenChange={setIsMultiSheetOpen}>
        <SheetContent side="bottom" className="h-[85vh]">
          <SheetHeader className="pb-4">
            <SheetTitle className="text-center">
              <span className="text-primary">{scannedBlocks.length} blocks found</span>
              <span className="text-muted-foreground text-sm font-normal ml-2">
                Tap rows to edit
              </span>
            </SheetTitle>
          </SheetHeader>

          {/* Action buttons */}
          <div className="flex justify-center gap-4 mb-4">
            <Button
              variant="ghost"
              onClick={handleClearAll}
              disabled={scannedBlocks.length === 0}
              className="text-destructive hover:text-destructive"
            >
              Clear All
            </Button>
            <Button
              onClick={handleSaveToLog}
              disabled={isAdding || scannedBlocks.length === 0}
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                "Save to Log"
              )}
            </Button>
          </div>

          {/* Table header */}
          <div className="grid grid-cols-[1fr_1.2fr_1fr_auto] gap-2 px-2 py-2 text-xs text-muted-foreground font-medium border-b">
            <div>Date</div>
            <div>Time</div>
            <div>Pay</div>
            <div className="w-8"></div>
          </div>

          {/* Block rows */}
          <div className="overflow-y-auto max-h-[calc(85vh-200px)]">
            {scannedBlocks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No blocks to display
              </div>
            ) : (
              scannedBlocks.map((block) => {
                const duplicate = isDuplicate(block);
                return (
                  <div
                    key={block.id}
                    className={`grid grid-cols-[1fr_1.2fr_1fr_auto] gap-2 px-2 py-3 border-b items-center cursor-pointer hover:bg-muted/50 transition-colors ${
                      duplicate ? "opacity-50" : ""
                    }`}
                    onClick={() => handleEditBlock(block)}
                  >
                    <div className="text-sm">
                      {formatDateShort(block.date)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatTimeRange(block.shiftStart, block.shiftEnd)}
                    </div>
                    <div className="text-sm font-medium text-green-500">
                      {formatCurrency(block.basePay)}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveBlock(block.id);
                      }}
                      className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
